import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Button, Grid, Tab } from 'semantic-ui-react';
import { formatToCash } from 'Common/FormHelper';
import _ from 'lodash';

import ReactTable from 'Common/ReactTableWrapper';
import KeyboardInput from 'Common/Components/Keyboard/KeyboardInput';
import { LAYOUT_TYPES } from 'Common/Components/Keyboard/constants';
import { setNotificationDialog } from 'Components/dialogs/state/actions';
import * as Helpers from 'Common/Helpers';

import {
  DENOMINATIONS,
  INITIAL_DENOMINATIONS_STATE,
  INVALID_AMOUNT_MESSAGE,
} from './constants';
import { computeChecks, computeCash, computeTotal } from './helpers/compute';
import { getValue, focusInput } from '../Keyboard/withKeyboard';

class CashCalculator extends React.PureComponent {
  static propTypes = {
    // Callback that will have the current total and updated denominations passed to it
    onChange: PropTypes.func,
    onDenominationsChange: PropTypes.func,
    onChecksChange: PropTypes.func,
    onTotalChange: PropTypes.func,
    // For initializing or setting the values of the calculator
    // Keys should match the ones defined in DENOMINATIONS
    denominations: PropTypes.object,
    checks: PropTypes.arrayOf(PropTypes.object),
    maxTableHeight: PropTypes.number,
    onGetChecksClick: PropTypes.func,
    loading: PropTypes.bool,
    containerNode: PropTypes.object,

    // Boolean to indicate if the keyboard inputs should adjust for the app sidebar
    appSidebar: PropTypes.bool,
  };

  static defaultProps = {
    onChange: () => {},
    denominations: {},
    onDenominationsChange: () => {},
    onChecksChange: () => {},
    onTotalChange: () => {},
    checks: [],
    maxTableHeight: 350,
    onGetChecksClick: () => {},
    loading: false,
    appSidebar: false,
    containerNode: null,
  };

  constructor(props) {
    super(props);
    const denominations = props.denominations;

    const initialDenominations = { ...INITIAL_DENOMINATIONS_STATE };

    // Initialize the denominations with provided values
    Object.keys(denominations).forEach(k => {
      if (k in denominations) {
        initialDenominations[k] = denominations[k];
      }
    });

    this.state = {
      denominations: initialDenominations,
      checks: props.checks || [],
      amount: '',
      description: '',
    };

    this.inputRefs = [React.createRef(), React.createRef()];
    this.activeInputIndex = null;
    this.editIndex = null;
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      checks: prevChecks,
      denominations: prevDenominations,
      onChecksChange,
      onDenominationsChange,
      onTotalChange,
    } = prevProps;
    const { checks, onChange, denominations } = this.props;

    const checksChanged = !_.isEqual(prevChecks, checks);
    const denominationsChange = !_.isEqual(prevDenominations, denominations);

    if (checksChanged || denominationsChange) {
      const total = computeTotal(denominations, checks);
      const prevTotal = computeTotal(prevDenominations, prevChecks);

      this.setState({ denominations, checks });

      onChange(total, denominations, checks);

      if (checksChanged) {
        onChecksChange(checks, total);
      }

      if (denominationsChange) {
        onDenominationsChange(denominations, total);
      }

      if (total !== prevTotal) {
        onTotalChange(total);
      }
    }
  }

  /**
   * Clears the input if its current value is 0
   */
  onDenominationFocus = denomination => e => {
    const { onChange } = this.props;
    const { checks, denominations } = this.state;

    const currentValue = Number(denominations[denomination.name]) || 0;

    if (currentValue === 0) {
      const newDenominations = { ...denominations, [denomination.name]: '' };

      const currentTotal = computeTotal(newDenominations, checks);
      this.setState({
        denominations: newDenominations,
      });
      onChange(currentTotal, newDenominations, checks);
    }
  };

  /**
   * Handler for when a denomination is changed via user input
   */
  onDenominationChange = denomination => e => {
    const { onChange } = this.props;
    const { checks, denominations } = this.state;
    const value = getValue(e);

    const newDenominations = { ...denominations, [denomination.name]: value };

    const currentTotal = computeTotal(newDenominations, checks);
    this.setState({
      denominations: newDenominations,
    });
    onChange(currentTotal, newDenominations, checks);
  };

  /**
   * Adjusts the precision of the input on blur
   */
  onDenominationBlur = denomination => e => {
    const { name } = denomination;

    const newDenominations = { ...this.state.denominations };
    newDenominations[name] = Math.round(Number(newDenominations[name]));

    this.setState({ denominations: newDenominations });
  };

  /*
   * Increments the total for a specific denomination by 1
   */
  onAddCash = denomination => () => {
    const { onChange } = this.props;
    const { checks } = this.state;
    const { name } = denomination;

    const newDenominations = { ...this.state.denominations };
    const currentValue = Number(newDenominations[name]);

    newDenominations[name] = currentValue + 1;

    const currentTotal = computeTotal(newDenominations, checks);
    this.setState({
      denominations: newDenominations,
    });
    onChange(currentTotal, newDenominations, checks);
  };

  /**
   * Clears the calculator inputs
   */
  onClearClick = () => {
    const { onChange } = this.props;
    const denominations = { ...INITIAL_DENOMINATIONS_STATE };

    const total = computeTotal(denominations, this.state.checks);

    this.setState({ denominations });
    onChange(total, denominations, this.state.checks);
  };

  renderDenominationInput(denomination) {
    const { containerNode } = this.props;
    const { denominations } = this.state;
    const { name } = denomination;
    return (
      <Grid.Column key={name} widescreen={3} tablet={4}>
        <Grid.Row>
          <Button fluid type="button" onClick={this.onAddCash(denomination)}>
            {denomination.label}
          </Button>
          <KeyboardInput
            type="number"
            onChange={this.onDenominationChange(denomination)}
            onFocus={this.onDenominationFocus(denomination)}
            name={name}
            value={denominations[name]}
            onBlur={this.onDenominationBlur(denomination)}
            min={0}
            fluid
            layoutType={LAYOUT_TYPES.NUMERIC}
            usePortal
            containerNode={containerNode}
          />
        </Grid.Row>
      </Grid.Column>
    );
  }

  get cashTab() {
    return (
      <Tab.Pane>
        <Grid>
          {/* Empty row to pad */}
          <Grid.Row />
          {Object.values(DENOMINATIONS).map(d =>
            this.renderDenominationInput(d)
          )}
          <Grid.Column widescreen={3} tablet={4}>
            <Grid.Row>
              <Button
                fluid
                type="button"
                onClick={this.onClearClick}
                content="Clear"
              />
            </Grid.Row>
          </Grid.Column>
        </Grid>
      </Tab.Pane>
    );
  }

  onAmountChange = e => {
    let value = this.state.amount;
    if (!e.target) {
      // From keyboard component
      value = e;
    } else {
      value = e.target.value;
    }

    this.setState({ amount: value });
  };

  onAmountBlur = e => {
    const { amount } = this.state;

    this.setState({ amount: formatToCash(amount) });
  };

  onDescriptionChange = e => {
    let value = this.state.description;
    if (!e.target) {
      // From keyboard component
      value = e;
    } else {
      value = e.target.value;
    }

    this.setState({ description: value });
  };

  addCheck = () => {
    const { onChange, setNotificationDialog } = this.props;
    const { denominations, amount, description } = this.state;

    const parsedAmount = parseFloat(amount);

    if (parsedAmount > 999.99) {
      setNotificationDialog(INVALID_AMOUNT_MESSAGE);
      return;
    }

    let newChecks = [];

    if (this.editIndex !== null) {
      newChecks = [...this.state.checks];
      newChecks[this.editIndex] = { amount: parsedAmount, description };
      this.editIndex = null;
    } else {
      newChecks = [...this.state.checks, { amount: parsedAmount, description }];
    }

    const total = computeTotal(denominations, newChecks);

    this.setState({
      checks: newChecks,
      amount: '',
      description: '',
    });
    onChange(total, denominations, newChecks);
  };

  clearChecks = () => {
    const { onChange } = this.props;
    const { denominations } = this.state;
    const newChecks = [];
    const total = computeTotal(denominations, newChecks);

    this.setState({ checks: newChecks });
    this.editIndex = null;
    onChange(total, denominations, newChecks);
  };

  getChecks = () => {
    this.props.onGetChecksClick();
    this.editIndex = null;
  };

  onKeyboardInputFocus = index => e => {
    this.activeInputIndex = index;
  };

  onKeyboardInputTab = () => {
    const nextInputIndex = this.activeInputIndex + 1;

    if (nextInputIndex !== this.inputRefs.length) {
      focusInput(this.inputRefs[nextInputIndex]);
    }
  };

  get checksTab() {
    const { loading, maxTableHeight, appSidebar, containerNode } = this.props;
    const { checks, amount, description } = this.state;
    const columns = [
      {
        Header: 'Amount',
        accessor: 'amount',
        Cell: props => <div>{`$${formatToCash(props.value)}`}</div>,
      },
      {
        Header: 'Description',
        accessor: 'description',
      },
    ];

    const disableAddButton = parseFloat(amount) === 0;

    return (
      <Tab.Pane>
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Grid.Row>
                <ReactTable
                  data={checks}
                  columns={columns}
                  loading={loading}
                  showPagination={false}
                  menu={false}
                  minRows={3}
                  style={{
                    maxHeight: maxTableHeight,
                  }}
                  getTbodyProps={() => ({
                    style: {
                      overflow: 'hidden',
                    },
                  })}
                  onClick={this.onCheckRowClick}
                  getTdProps={(state, rowInfo, column, instance) => {
                    return {
                      onClick: (e, handleOriginal) => {
                        if (handleOriginal) {
                          handleOriginal();
                        }

                        if (rowInfo) {
                          const {
                            original: { amount, description },
                          } = rowInfo;
                          this.editIndex = rowInfo.index;

                          this.setState({
                            amount: formatToCash(amount),
                            description,
                          });
                        }
                      },
                    };
                  }}
                />
              </Grid.Row>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3}>
              <Button content="Clear Checks" onClick={this.clearChecks} fluid />
            </Grid.Column>
            <Grid.Column width={3}>
              <Button
                content="Get Checks"
                onClick={this.getChecks}
                fluid
                loading={loading}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <KeyboardInput
                onChange={this.onAmountChange}
                onBlur={this.onAmountBlur}
                value={amount}
                placeholder="Amount"
                fluid
                maxLength={7}
                appSidebar={appSidebar}
                usePortal
                layoutType={LAYOUT_TYPES.NUMERIC}
                ref={this.inputRefs[0]}
                onFocus={this.onKeyboardInputFocus(0)}
                onTabPress={this.onKeyboardInputTab}
                containerNode={containerNode}
              />
            </Grid.Column>
            <Grid.Column width={4}>
              <KeyboardInput
                onChange={this.onDescriptionChange}
                maxLength={25}
                value={description}
                placeholder="Description"
                fluid
                appSidebar={appSidebar}
                usePortal
                ref={this.inputRefs[1]}
                onFocus={this.onKeyboardInputFocus(1)}
                onTabPress={this.onKeyboardInputTab}
                containerNode={containerNode}
              />
            </Grid.Column>
            <Grid.Column width={2}>
              <Button
                content="Add"
                onClick={this.addCheck}
                disabled={disableAddButton}
              />
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Tab.Pane>
    );
  }

  get panes() {
    const { checks, denominations } = this.props;

    return [
      {
        menuItem: `Cash $${Helpers.Formatters.currency(
          computeCash(denominations)
        )}`,
        render: () => this.cashTab,
      },
      {
        menuItem: `Checks $${Helpers.Formatters.currency(
          computeChecks(checks)
        )}`,
        render: () => this.checksTab,
      },
    ];
  }

  render() {
    return <Tab panes={this.panes} />;
  }
}

const mapDispatchToProps = {
  setNotificationDialog,
};

export default connect(null, mapDispatchToProps)(CashCalculator);



// WEBPACK FOOTER //
// ./src/common/Components/CashCalculator/CashCalculator.js